import {
  encryptionService,
  ensureEncryptionReady,
} from "../config/axiosConfig";
import { STORAGE_KEYS } from "../services/encrypt-decrypt";
import {
  holdFhirClient,
  releaseFhirClient,
} from "../services/fhir-client-holder";
import { setProviderNPI } from "../store/providerReducer";
import {
  setFhirFailed,
  setFhirInitialized,
  setFhirInitializing,
  setFhirMrn,
  setFHIRPatientAndPractitioner,
} from "../store/fhirReducer";
import { AppDispatch } from "../store/store";

export interface FhirPractitioner {
  id: string;
  name?: Array<{
    family?: string;
    given?: string[];
    text?: string;
  }>;
  identifier?: Array<{
    value?: string;
    type?: { text?: string };
  }>;
  qualification?: Array<{
    code?: { text?: string };
  }>;
}

export const getPatientId = (patientData: any): string | null => {
  if (patientData?.id) return patientData.id;
  if (patientData?.data?.id) return patientData.data.id;
  if (patientData?.entry?.[0]?.resource?.id) {
    return patientData.entry[0].resource.id;
  }
  if (patientData?.resource?.id) return patientData.resource.id;
  return null;
};

export const getPatientResource = (patientData: any): any => {
  if (patientData?.resourceType === "Patient") return patientData;
  if (patientData?.data?.resourceType === "Patient") return patientData.data;
  if (patientData?.entry?.[0]?.resource?.resourceType === "Patient") {
    return patientData.entry[0].resource;
  }
  if (patientData?.resource?.resourceType === "Patient") {
    return patientData.resource;
  }
  if (patientData?.id && (patientData?.name || patientData?.identifier)) {
    return patientData;
  }
  return patientData;
};

export const getPractitionerNameFromResource = (
  practitioner?: FhirPractitioner | null
): string => {
  if (!practitioner?.name?.[0]) return "";

  const name = practitioner.name[0];
  if (name.text?.trim()) return name.text.trim();

  const given = name.given?.join(" ")?.trim() ?? "";
  const family = name.family?.trim() ?? "";
  return `${given} ${family}`.trim();
};

export const getPractitionerNPI = (
  practitioner: FhirPractitioner | null
): string => {
  if (!practitioner?.identifier?.length) return "";

  const npiIdentifier = practitioner.identifier.find(
    (id) => id.type?.text === "NPI"
  );
  if (npiIdentifier?.value) return npiIdentifier.value;

  const externalIdentifier = practitioner.identifier.find(
    (id) =>
      id.type?.text === "EXTERNAL" &&
      id.value &&
      /^[0-9]+$/.test(id.value.trim())
  );

  return externalIdentifier?.value?.trim() || "";
};

export const validateAndExtractMrn = (
  patientData: any
): { mrn: string | null; error: string | null } => {
  try {
    if (!patientData) {
      return { mrn: null, error: "Patient data is not available" };
    }

    if (!patientData.identifier || !Array.isArray(patientData.identifier)) {
      return { mrn: null, error: "Patient identifiers are missing or invalid" };
    }

    const mrnIdentifier = patientData.identifier.find(
      (identifier: any) => identifier.type?.text === "MRN"
    );

    if (
      mrnIdentifier?.value &&
      typeof mrnIdentifier.value === "string" &&
      mrnIdentifier.value.trim().length > 0
    ) {
      return { mrn: mrnIdentifier.value.trim(), error: null };
    }

    const epiIdentifier = patientData.identifier.find(
      (identifier: any) => identifier.type?.text === "EPI"
    );

    if (!epiIdentifier?.value || typeof epiIdentifier.value !== "string") {
      return {
        mrn: null,
        error: "Neither MRN nor EPI identifier found in patient data",
      };
    }

    const epiValue = epiIdentifier.value.trim();
    if (!epiValue.length) {
      return { mrn: null, error: "EPI value is empty" };
    }

    return { mrn: epiValue, error: null };
  } catch (error) {
    console.error("Error extracting MRN/EPI:", error);
    return {
      mrn: null,
      error: "Failed to extract MRN or EPI from patient data",
    };
  }
};

export const fetchPractitionerFromClient = async (
  client: any
): Promise<FhirPractitioner | null> => {
  if (!client.user) return null;

  let practitioner: FhirPractitioner | null = null;

  if (client.user.fhirUser) {
    try {
      practitioner = await client.request(client.user.fhirUser);
    } catch (error) {
      console.error("Error fetching practitioner from fhirUser:", error);
    }
  }

  if (!practitioner && client.user.id) {
    try {
      const searchResult = await client.request(
        `Practitioner?identifier=${client.user.id}`
      );
      if (searchResult?.entry?.length > 0) {
        practitioner = searchResult.entry[0].resource;
      }
    } catch (error) {
      console.error("Error searching practitioner by user ID:", error);
    }
  }

  if (!practitioner && client.user.profile) {
    try {
      const profile = client.user.profile;
      const searchParams: string[] = [];

      if (profile.family_name && profile.given_name) {
        searchParams.push(`family=${encodeURIComponent(profile.family_name)}`);
        searchParams.push(`given=${encodeURIComponent(profile.given_name)}`);
      } else if (profile.name) {
        searchParams.push(`name=${encodeURIComponent(profile.name)}`);
      }

      if (searchParams.length > 0) {
        const searchResult = await client.request(
          `Practitioner?${searchParams.join("&")}`
        );
        if (searchResult?.entry?.length > 0) {
          practitioner = searchResult.entry[0].resource;
        }
      }
    } catch (error) {
      console.error("Error searching practitioner by profile:", error);
    }
  }

  return practitioner;
};

export const exchangeFhirTokens = async (
  accessToken: string,
  idToken: string,
  baseUrl: string
): Promise<boolean> => {
  if (localStorage.getItem(STORAGE_KEYS.JWT_TOKEN)) {
    return true;
  }

  const apiBaseUrl = process.env.REACT_APP_BASE_URL;
  const tokenExchangeResponse = await fetch(
    `${apiBaseUrl}crypto/auth/token-exchange`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "id-token": idToken,
        "access-token": accessToken,
        "base-url": baseUrl,
      },
    }
  );

  if (!tokenExchangeResponse.ok) {
    const errorText = await tokenExchangeResponse.text();
    throw new Error(`Token exchange failed: ${errorText}`);
  }

  const exchangeResult = await tokenExchangeResponse.json();
  if (!exchangeResult.data) {
    throw new Error("Invalid response structure from token exchange");
  }

  localStorage.setItem(
    STORAGE_KEYS.JWT_TOKEN,
    exchangeResult.data.access_token || ""
  );
  localStorage.setItem(STORAGE_KEYS.EPIC_INTROSPECT_URL, baseUrl || "");

  // New JWT — discard stale encryption keys from a prior session.
  localStorage.removeItem(STORAGE_KEYS.SYMMETRIC_KEY);
  localStorage.removeItem(STORAGE_KEYS.KEY_ID);
  localStorage.removeItem(STORAGE_KEYS.PUBLIC_KEY);
  encryptionService.reset();

  if (exchangeResult.data.refresh_token) {
    localStorage.setItem(
      STORAGE_KEYS.REFRESH_TOKEN,
      exchangeResult.data.refresh_token
    );
  }

  const now = Date.now();
  const accessExpiresInSec = exchangeResult.data.expires_in || 7200;
  const refreshExpiresInSec = exchangeResult.data.refresh_expires_in || 604800;

  localStorage.setItem(
    STORAGE_KEYS.EXPIRES_IN,
    String(now + accessExpiresInSec * 1000)
  );
  localStorage.setItem(
    STORAGE_KEYS.REFRESH_EXPIRES_IN,
    String(now + refreshExpiresInSec * 1000)
  );

  return true;
};

export const initializeEncryptionIfNeeded = async (): Promise<void> => {
  await ensureEncryptionReady();
};

export const initializeFhirSession = async (
  dispatch: AppDispatch,
  FHIR: any
): Promise<{ client: any; patient: any; practitioner: FhirPractitioner | null }> => {
  dispatch(setFhirInitializing());

  const client = await FHIR.oauth2.ready();
  const accessToken = client?.state?.tokenResponse?.access_token;
  const idToken = client?.state?.tokenResponse?.id_token;
  const baseUrl = client?.state?.serverUrl;

  let tokenExchangeSuccess = false;
  if (accessToken && idToken && baseUrl) {
    try {
      tokenExchangeSuccess = await exchangeFhirTokens(
        accessToken,
        idToken,
        baseUrl
      );
    } catch (tokenError) {
      console.error("Token exchange error:", tokenError);
    }
  }

  if (!tokenExchangeSuccess) {
    throw new Error("Token exchange failed");
  }

  await initializeEncryptionIfNeeded();

  const rawPatientData = await client.patient.read();
  const patientResource = getPatientResource(rawPatientData);
  const patientId = getPatientId(rawPatientData);

  if (!patientId) {
    throw new Error("Could not extract patient ID from FHIR response");
  }

  const practitioner = await fetchPractitionerFromClient(client);
  const npi = getPractitionerNPI(practitioner);
  if (npi) {
    dispatch(setProviderNPI(npi));
  }

  dispatch(
    setFHIRPatientAndPractitioner({
      practitioner,
      patient: patientResource,
      encounter: client?.encounter?.id,
    })
  );

  const { mrn: extractedMrn } = validateAndExtractMrn(patientResource);
  dispatch(setFhirMrn(extractedMrn));
  holdFhirClient(client);
  dispatch(setFhirInitialized({ mrn: extractedMrn }));

  return { client, patient: patientResource, practitioner };
};

export const handleFhirInitializationFailure = (
  dispatch: AppDispatch,
  error: unknown
) => {
  console.error("Error initializing SMART on FHIR:", error);
  releaseFhirClient();
  dispatch(
    setFhirFailed(
      "Failed to initialize SMART on FHIR client. Please ensure you're accessing this from a compatible EHR system."
    )
  );
};
